import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import theme from "./Theme";
import { NavigationProvider } from "./shared/context/NavigationContext";
import { VisitProvider } from "./cadet/context/CurrentVisitContext";
import { useUser } from "./shared/hooks/useUser";
import { useLoginViewModel } from "./shared/viewModels/LoginViewModel";
import config from "./config";
import { logger } from "./shared/utils/logger";

const Login = React.lazy(() => import("./shared/ui/pages/Login"));
const RoleRoutes = React.lazy(() => import("./routes/RoleRoutes"));

function App() {
    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter basename={config.ENDPOINT}>
                <NavigationProvider>
                    <VisitProvider>
                        <AuthHandler />
                    </VisitProvider>
                </NavigationProvider>
            </BrowserRouter>
        </ThemeProvider>
    );
}

const AuthHandler = () => {
    const { user, updateUser } = useUser();
    const [isLoading, setIsLoading] = useState(true);
    const { loginWithSessionId } = useLoginViewModel();

    useEffect(() => {
        const expectedOrigin = "http://localhost:5500";

        // Login tramite Linkup
        const sendReadyMessage = () => {
            if (window.opener) {
                try {
                    window.opener.postMessage({ ready: true }, expectedOrigin);
                } catch (e) {
                    logger.error("Errore nell'invio del messaggio 'ready'", e);
                }
            }
        };

        const handleMessage = async (event: MessageEvent) => {
            if (event.origin !== expectedOrigin) {
                logger.warn("Ignorato messaggio da origine non sicura:", event.origin);
                return;
            }

            if (event.data?.ApolloSession) {
                setIsLoading(true);
                try {
                    await loginWithSessionId(event.data.ApolloSession);
                    // Aggiorna lo stato utente immediatamente
                    updateUser();
                } catch (error) {
                    logger.error("SSO login failed:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        window.addEventListener("message", handleMessage);
        sendReadyMessage();

        const heartbeat = setInterval(sendReadyMessage, 1000);
        const timeoutId = setTimeout(() => {
            clearInterval(heartbeat);
            setIsLoading(false);
            logger.warn("Timeout: nessun sessionId ricevuto");
        }, 1000);

        return () => {
            clearInterval(heartbeat);
            clearTimeout(timeoutId);
            window.removeEventListener("message", handleMessage);
        };
        // loginWithSessionId/updateUser non sono memoizzati (v. LoginViewModel.ts/useUser.ts) quindi
        // cambiano identità ad ogni render, ma il loro comportamento non dipende da alcuno stato per-render:
        // aggiungerli qui rieseguirebbe l'intero setup (listener/heartbeat/timeout dell'handshake SSO) ad
        // ogni render di AuthHandler invece che una sola volta al mount, senza alcun beneficio reale.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Gestione stati di caricamento
    if (isLoading) {
        return <div>Caricamento...</div>;
    }

    // Redirect in base allo stato dell'utente
    if (!user || !user.type) {
        return (
            <Suspense fallback={<div>Caricamento...</div>}>
                <Routes>
                    <Route path="/*" element={<Login />} />
                </Routes>
            </Suspense>
        );
    }

    return (
        <Suspense fallback={<div>Caricamento...</div>}>
            <Routes>
                <Route path="/*" element={<RoleRoutes />} />
            </Routes>
        </Suspense>
    );
};

export default App;
